import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const TaskGroupsManagement = () => {
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    nazwa: '',
    opis: '',
    kolor: 'var(--info)',
    ikona: 'folder',
    location_id: '',
    is_global: false
  });

  // Dostępne kolory
  const colors = [
    'var(--info)', 'var(--success)', 'var(--warning)', 'var(--danger)', 'var(--purple)',
    'var(--orange)', 'var(--cyan)', 'var(--primary)', 'var(--primary-dark)', 'var(--text-muted)'
  ];

  // Dostępne ikony
  const icons = [
    { value: 'folder', label: '📁 Folder' },
    { value: 'star', label: '⭐ Gwiazda' },
    { value: 'flag', label: '🚩 Flaga' },
    { value: 'heart', label: '❤️ Serce' },
    { value: 'lightning', label: '⚡ Błyskawica' },
    { value: 'fire', label: '🔥 Ogień' },
    { value: 'clock', label: '⏰ Zegar' },
    { value: 'check', label: '✅ Check' },
    { value: 'home', label: '🏠 Dom' },
    { value: 'briefcase', label: '💼 Praca' },
    { value: 'shopping', label: '🛒 Zakupy' },
    { value: 'money', label: '💰 Pieniądze' }
  ];

  const getIconEmoji = (iconName) => {
    const icon = icons.find(i => i.value === iconName);
    return icon ? icon.label.split(' ')[0] : '📁';
  };

  // Pobierz dane
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsRes, locationsRes] = await Promise.all([
        api.get('/tasks/groups'),
        api.get('/locations/')
      ]);

      const groupsData = groupsRes.data;
      const locationsData = locationsRes.data;

      if (groupsData.success) {
        setGroups(groupsData.data || []);
      }
      
      if (locationsData.success || Array.isArray(locationsData)) {
        setLocations(locationsData.success ? locationsData.data : locationsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Otwórz modal do dodawania
  const openAddModal = () => {
    setFormData({
      nazwa: '',
      opis: '',
      kolor: 'var(--info)',
      ikona: 'folder',
      location_id: '',
      is_global: false
    });
    setEditingGroup(null);
    setShowModal(true);
  };

  // Otwórz modal do edycji
  const openEditModal = (group) => {
    setFormData({
      nazwa: group.nazwa || '',
      opis: group.opis || '',
      kolor: group.kolor || 'var(--info)',
      ikona: group.ikona || 'folder',
      location_id: group.location_id || '',
      is_global: group.is_global === 1 || group.is_global === true
    });
    setEditingGroup(group);
    setShowModal(true);
  };

  // Zapisz grupę
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nazwa.trim()) {
      toast.error('Nazwa grupy jest wymagana');
      return;
    }

    try {
      const bodyData = {
        ...formData,
        location_id: formData.is_global ? null : formData.location_id || null
      };

      const response = editingGroup
        ? await api.put(`/tasks/groups/${editingGroup.id}`, bodyData)
        : await api.post('/tasks/groups', bodyData);

      const data = response.data;
      if (data.success) {
        setShowModal(false);
        fetchData();
      } else {
        toast.error('Błąd: ' + (data.error || 'Nieznany błąd'));
      }
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Błąd zapisu: ' + error.message);
    }
  };

  // Usuń grupę
  const handleDelete = async (groupId) => {
    if (!window.confirm('Czy na pewno usunąć tę grupę? Zadania przypisane do tej grupy zostaną odpięte.')) {
      return;
    }

    try {
      const response = await api.delete(`/tasks/groups/${groupId}`);
      const data = response.data;
      if (data.success) {
        fetchData();
      } else {
        toast.error('Błąd: ' + (data.error || 'Nieznany błąd'));
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Błąd usuwania: ' + error.message);
    }
  };

  // Grupuj grupy według lokalizacji
  const groupedByLocation = groups.reduce((acc, group) => {
    const key = group.is_global ? 'global' : (group.location_id || 'no_location');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(group);
    return acc;
  }, {});

  const getLocationName = (locationId) => {
    if (locationId === 'global') return '🌐 Grupy globalne';
    if (locationId === 'no_location') return '❓ Bez lokalizacji';
    const loc = locations.find(l => l.id === parseInt(locationId));
    return loc ? `📍 ${loc.nazwa}` : `Lokalizacja ${locationId}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p>Ładowanie grup zadań...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, var(--purple), var(--purple))',
        borderRadius: '8px',
        color: 'white'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            📋 Grupy zadań
          </h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
            Zarządzaj grupami zadań dla lokalizacji
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '6px 12px',
            background: 'var(--bg-secondary)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          ➕ Nowa grupa
        </button>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          padding: '1rem',
          background: 'var(--info-light)',
          borderRadius: '8px',
          border: '1px solid var(--info-light)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--info)' }}>
            {groups.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--info)' }}>Wszystkie grupy</div>
        </div>
        <div style={{
          padding: '1rem',
          background: 'var(--success-light)',
          borderRadius: '8px',
          border: '1px solid var(--success-light)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--success)' }}>
            {groups.filter(g => g.is_global).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--success)' }}>Globalne</div>
        </div>
        <div style={{
          padding: '1rem',
          background: 'var(--warning-light)',
          borderRadius: '8px',
          border: '1px solid var(--warning-light)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--warning)' }}>
            {groups.filter(g => !g.is_global && g.location_id).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--warning)' }}>Dla lokalizacji</div>
        </div>
        <div style={{
          padding: '1rem',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px solid var(--border)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
            {groups.reduce((sum, g) => sum + (g.task_count || 0), 0)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Zadań w grupach</div>
        </div>
      </div>

      {/* Groups list by location */}
      {Object.keys(groupedByLocation).length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--bg)',
          borderRadius: '8px',
          border: '1px dashed var(--border)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
          <h3 style={{ margin: 0, color: 'var(--text-secondary)' }}>Brak grup zadań</h3>
          <p style={{ color: 'var(--text-muted)', marginTop: '0.5rem' }}>
            Kliknij "Nowa grupa" aby utworzyć pierwszą grupę
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* First show global groups */}
          {groupedByLocation['global'] && (
            <div>
              <h3 style={{
                margin: '0 0 0.75rem',
                padding: '0.5rem 1rem',
                background: 'var(--success-light)',
                borderRadius: '6px',
                fontSize: '0.9rem',
                color: 'var(--success)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🌐 Grupy globalne
                <span style={{
                  border: '1px solid var(--success)',
                  color: 'var(--success)',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem'
                }}>
                  {groupedByLocation['global'].length}
                </span>
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                {groupedByLocation['global'].map(group => (
                  <GroupCard 
                    key={group.id} 
                    group={group} 
                    onEdit={() => openEditModal(group)}
                    onDelete={() => handleDelete(group.id)}
                    getIconEmoji={getIconEmoji}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Then location-specific groups */}
          {Object.entries(groupedByLocation)
            .filter(([key]) => key !== 'global' && key !== 'no_location')
            .map(([locationId, locationGroups]) => (
              <div key={locationId}>
                <h3 style={{
                  margin: '0 0 0.75rem',
                  padding: '0.5rem 1rem',
                  background: 'var(--info-light)',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  color: 'var(--info)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {getLocationName(locationId)}
                  <span style={{
                    border: '1px solid var(--info)',
                    color: 'var(--info)',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: '0.75rem'
                  }}>
                    {locationGroups.length}
                  </span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                  {locationGroups.map(group => (
                    <GroupCard 
                      key={group.id} 
                      group={group} 
                      onEdit={() => openEditModal(group)}
                      onDelete={() => handleDelete(group.id)}
                      getIconEmoji={getIconEmoji}
                    />
                  ))}
                </div>
              </div>
            ))}

          {/* Groups without location */}
          {groupedByLocation['no_location'] && (
            <div>
              <h3 style={{
                margin: '0 0 0.75rem',
                padding: '0.5rem 1rem',
                background: 'var(--bg)',
                borderRadius: '6px',
                fontSize: '0.9rem',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                ❓ Bez przypisanej lokalizacji
                <span style={{
                  border: '1px solid var(--text-muted)',
                  color: 'var(--text-muted)',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem'
                }}>
                  {groupedByLocation['no_location'].length}
                </span>
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                {groupedByLocation['no_location'].map(group => (
                  <GroupCard 
                    key={group.id} 
                    group={group} 
                    onEdit={() => openEditModal(group)}
                    onDelete={() => handleDelete(group.id)}
                    getIconEmoji={getIconEmoji}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '8px',
            width: '450px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1rem',
              background: editingGroup 
                ? 'linear-gradient(135deg, var(--orange), var(--orange))' 
                : 'linear-gradient(135deg, var(--purple), var(--purple))',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <div style={{
                width: '2.5rem',
                height: '2.5rem',
                background: 'var(--bg-secondary)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem'
              }}>
                {editingGroup ? '✏️' : '➕'}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                  {editingGroup ? 'Edytuj grupę' : 'Nowa grupa zadań'}
                </h3>
                <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>
                  {editingGroup ? 'Zmień ustawienia grupy' : 'Utwórz nową grupę dla zadań'}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  
                  fontSize: '1.5rem',
                  color: '#fff',
                  padding: '0.25rem'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} style={{ padding: '1rem' }}>
              {/* Nazwa */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Nazwa grupy *
                </label>
                <input
                  type="text"
                  value={formData.nazwa}
                  onChange={(e) => setFormData({ ...formData, nazwa: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                  placeholder="np. Pilne zadania, Do zrobienia dziś..."
                  autoFocus
                />
              </div>

              {/* Opis */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Opis
                </label>
                <textarea
                  value={formData.opis}
                  onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    minHeight: '60px',
                    resize: 'vertical'
                  }}
                  placeholder="Opcjonalny opis grupy..."
                />
              </div>

              {/* Kolor i ikona */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                    Kolor
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {colors.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, kolor: color })}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          background: color,
                          border: formData.kolor === color ? '2px solid var(--text)' : '2px solid transparent'
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                    Ikona
                  </label>
                  <select
                    value={formData.ikona}
                    onChange={(e) => setFormData({ ...formData, ikona: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    {icons.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Lokalizacja i globalność */}
              <div style={{
                padding: '1rem',
                background: 'var(--bg)',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  📍 Przypisanie do lokalizacji
                </h4>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.is_global}
                      onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    🌐 Grupa globalna (widoczna we wszystkich lokalizacjach)
                  </label>
                </div>

                {!formData.is_global && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                      Lokalizacja
                    </label>
                    <select
                      value={formData.location_id}
                      onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="">-- Wybierz lokalizację --</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.nazwa}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  background: formData.is_global ? 'var(--success-light)' : (formData.location_id ? 'var(--info-light)' : 'var(--warning-light)'),
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  color: formData.is_global ? 'var(--success)' : (formData.location_id ? 'var(--info)' : 'var(--warning)')
                }}>
                  {formData.is_global 
                    ? '🌐 Ta grupa będzie widoczna dla wszystkich lokalizacji'
                    : formData.location_id 
                      ? '📍 Ta grupa będzie widoczna tylko dla wybranej lokalizacji'
                      : '⚠️ Wybierz lokalizację lub ustaw jako globalną'}
                </div>
              </div>

              {/* Preview */}
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: 'var(--bg)',
                borderRadius: '6px',
                border: '1px solid var(--border)'
              }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                  Podgląd
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem',
                  background: 'var(--card-bg)',
                  borderRadius: '4px',
                  border: `2px solid ${formData.kolor}`
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{getIconEmoji(formData.ikona)}</span>
                  <span style={{ fontWeight: 500, color: formData.kolor }}>
                    {formData.nazwa || 'Nazwa grupy'}
                  </span>
                  {formData.is_global && (
                    <span style={{ fontSize: '0.7rem', background: 'var(--success-light)', color: 'var(--success)', padding: '0.15rem 0.4rem', borderRadius: '3px' }}>
                      🌐 Globalna
                    </span>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    border: '1px solid var(--text-muted)',
                    color: 'var(--text-muted)',
                    fontSize: '12px'
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2,
                    padding: '6px 12px',
                    border: editingGroup
                      ? '1px solid var(--orange)'
                      : '1px solid var(--purple)',
                    color: editingGroup ? 'var(--orange)' : 'var(--purple)',
                    fontSize: '12px',
                    fontWeight: 500
                  }}
                >
                  {editingGroup ? '💾 Zapisz zmiany' : '➕ Utwórz grupę'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Group Card Component
const GroupCard = ({ group, onEdit, onDelete, getIconEmoji }) => {
  return (
    <div style={{
      padding: '1rem',
      background: 'var(--card-bg)',
      borderRadius: '8px',
      border: `2px solid ${group.kolor || 'var(--border)'}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: `${group.kolor}20`,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.25rem',
          flexShrink: 0
        }}>
          {getIconEmoji(group.ikona)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <h4 style={{ 
              margin: 0, 
              fontSize: '0.95rem', 
              fontWeight: 600,
              color: group.kolor || 'var(--text)'
            }}>
              {group.nazwa}
            </h4>
            {group.is_global === 1 && (
              <span style={{
                fontSize: '0.65rem',
                background: 'var(--success-light)',
                color: 'var(--success)',
                padding: '0.1rem 0.4rem',
                borderRadius: '3px'
              }}>
                🌐 Globalna
              </span>
            )}
          </div>
          {group.opis && (
            <p style={{ 
              margin: '0.25rem 0 0', 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {group.opis}
            </p>
          )}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem', 
            marginTop: '0.5rem',
            fontSize: '0.7rem',
            color: 'var(--text-muted)'
          }}>
            <span>📋 {group.task_count || 0} zadań</span>
            {group.location_nazwa && (
              <span>📍 {group.location_nazwa}</span>
            )}
          </div>
        </div>
      </div>
      
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginTop: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border)'
      }}>
        <button
          onClick={onEdit}
          style={{
            flex: 1,
            padding: '4px 10px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            fontSize: '11px'
          }}
        >
          ✏️ Edytuj
        </button>
        <button
          onClick={onDelete}
          style={{
            flex: 1,
            padding: '4px 10px',
            background: 'var(--danger-light)',
            border: '1px solid var(--danger-light)',
            fontSize: '11px',
            color: 'var(--danger)'
          }}
        >
          🗑️ Usuń
        </button>
      </div>
    </div>
  );
};

export default TaskGroupsManagement;
